import {
  and,
  collector_profile,
  eq,
  type PowerSyncDrizzleDB,
  profile_favorite,
  profile_featured,
  profile_tag,
} from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { canAddFeatured, type InteractionPermission, type ProfileVisibility } from "./model";

/**
 * 🧑‍🎨 PERFIL DEL COLECCIONISTA — escrituras del cliente sobre la VISTA
 * local-first. Como en la colección (gotcha #15), la unicidad la resuelve el
 * CLIENTE (SELECT existente → UPDATE|INSERT); el servidor reaplica por id con
 * el user_id ESTAMPADO (aislamiento). Las vistas PowerSync no admiten UPSERT.
 */

export interface ProfileSettingsPatch {
  display_name?: string | null;
  bio?: string | null;
  frame_id?: string | null;
  is_private?: boolean;
  collection_visibility?: ProfileVisibility;
  wishlist_visibility?: ProfileVisibility;
  show_location?: boolean;
  comment_permission?: InteractionPermission;
  tag_permission?: InteractionPermission;
}

/**
 * UPDATE-or-INSERT del perfil (privacidad + marco + bio). A lo sumo una fila por
 * usuario (índice único `collector_profile_user_key`). Los booleanos se guardan
 * como 0/1 (SQLite). Devuelve el id de la fila.
 */
export async function saveProfileSettingsMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  patch: ProfileSettingsPatch,
): Promise<string> {
  const now = nowISO();
  const existing = await getProfileRow(db, userId);

  const values = {
    ...(patch.display_name !== undefined ? { display_name: patch.display_name } : {}),
    ...(patch.bio !== undefined ? { bio: patch.bio } : {}),
    ...(patch.frame_id !== undefined ? { frame_id: patch.frame_id } : {}),
    ...(patch.is_private !== undefined ? { is_private: patch.is_private ? 1 : 0 } : {}),
    ...(patch.collection_visibility !== undefined
      ? { collection_visibility: patch.collection_visibility }
      : {}),
    ...(patch.wishlist_visibility !== undefined
      ? { wishlist_visibility: patch.wishlist_visibility }
      : {}),
    ...(patch.show_location !== undefined ? { show_location: patch.show_location ? 1 : 0 } : {}),
    ...(patch.comment_permission !== undefined
      ? { comment_permission: patch.comment_permission }
      : {}),
    ...(patch.tag_permission !== undefined ? { tag_permission: patch.tag_permission } : {}),
  };

  if (existing) {
    await db
      .update(collector_profile)
      .set({ ...values, updated_at: now })
      .where(and(eq(collector_profile.user_id, userId), eq(collector_profile.id, existing.id)));
    return existing.id;
  }

  const id = generateUUID();
  await db.insert(collector_profile).values({
    id,
    user_id: userId,
    display_name: patch.display_name ?? null,
    bio: patch.bio ?? null,
    frame_id: patch.frame_id ?? null,
    is_private: patch.is_private ? 1 : 0,
    collection_visibility: patch.collection_visibility ?? "everyone",
    wishlist_visibility: patch.wishlist_visibility ?? "followers",
    show_location: patch.show_location ? 1 : 0,
    comment_permission: patch.comment_permission ?? "everyone",
    tag_permission: patch.tag_permission ?? "followers",
    created_at: now,
    updated_at: now,
  });
  return id;
}

async function getProfileRow(db: PowerSyncDrizzleDB, userId: string) {
  const rows = await db
    .select({ id: collector_profile.id })
    .from(collector_profile)
    .where(eq(collector_profile.user_id, userId))
    .limit(1);
  return rows[0];
}

/** Fija (o quita, con `null`) el marco estacional. Atajo sobre el UPDATE-or-INSERT. */
export async function setFrameMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  frameId: string | null,
): Promise<string> {
  return saveProfileSettingsMutation(db, userId, { frame_id: frameId });
}

export interface ToggleFeaturedResult {
  featured: boolean;
  /** true si se rechazó por estar en el tope de 5 (nada cambió). */
  atCap: boolean;
}

/**
 * Alterna un producto en los DESTACADOS respetando el tope de 5. Si ya está →
 * lo quita. Si no y hay hueco → lo añade al final. Si no y está en el tope →
 * no hace nada y devuelve `atCap: true`.
 */
export async function toggleFeaturedMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  productId: string,
): Promise<ToggleFeaturedResult> {
  const rows = await db
    .select({
      id: profile_featured.id,
      product_id: profile_featured.product_id,
      sort_order: profile_featured.sort_order,
    })
    .from(profile_featured)
    .where(eq(profile_featured.user_id, userId));

  const current = rows.find((r) => r.product_id === productId);
  if (current) {
    await db
      .delete(profile_featured)
      .where(and(eq(profile_featured.user_id, userId), eq(profile_featured.id, current.id)));
    return { featured: false, atCap: false };
  }

  if (!canAddFeatured(rows.length)) {
    return { featured: false, atCap: true };
  }

  const now = nowISO();
  const nextOrder = rows.reduce((max, r) => Math.max(max, (r.sort_order ?? 0) + 1), 0);
  await db.insert(profile_featured).values({
    id: generateUUID(),
    user_id: userId,
    product_id: productId,
    sort_order: nextOrder,
    created_at: now,
    updated_at: now,
  });
  return { featured: true, atCap: false };
}

/** Alterna un producto en los FAVORITOS (insert/delete). Devuelve el estado final. */
export async function toggleFavoriteMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  productId: string,
): Promise<{ favorited: boolean }> {
  const existing = await db
    .select({ id: profile_favorite.id })
    .from(profile_favorite)
    .where(and(eq(profile_favorite.user_id, userId), eq(profile_favorite.product_id, productId)))
    .limit(1);

  if (existing[0]) {
    await db
      .delete(profile_favorite)
      .where(and(eq(profile_favorite.user_id, userId), eq(profile_favorite.id, existing[0].id)));
    return { favorited: false };
  }

  await db.insert(profile_favorite).values({
    id: generateUUID(),
    user_id: userId,
    product_id: productId,
    created_at: nowISO(),
  });
  return { favorited: true };
}

/** Añade una etiqueta a un producto del usuario. */
export async function addTagMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  productId: string,
  label: string,
): Promise<string> {
  const id = generateUUID();
  await db.insert(profile_tag).values({
    id,
    user_id: userId,
    product_id: productId,
    label,
    created_at: nowISO(),
  });
  return id;
}

/** Quita una etiqueta por id. */
export async function removeTagMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  await db.delete(profile_tag).where(and(eq(profile_tag.user_id, userId), eq(profile_tag.id, id)));
}
